import {
  DownOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileZipOutlined,
} from '@ant-design/icons';
import { Space, Tag } from 'antd';
import { type MenuItemGroupType } from 'antd5/es/menu/interface';
import { assign, flatten, keyBy, lowerCase, startCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Dropdown, Modal, Tooltip, useMessage } from 'components/ui';
import { PollType, usePollingReportQuery } from 'horizon/routes/Reports/usePollingReportQuery';
import {
  type AtlasGql_CreateReportInput,
  type AtlasGql_ReportArgument,
  type InputMaybe,
  AtlasGqlReportDeliveryStatus,
  AtlasGqlReportStatus,
  useCreateReportMutation,
  useCreateReportsMutation,
  useGetReportDeliveryModesQuery,
} from 'types/atlas-graphql';

import { type SubmitReportTemplateProp, AdvancedReportForm } from './AdvancedReportForm';
import {
  type DeliveryModeArgumentOption,
  type ReportContext,
  type ReportTemplatesForContext,
} from './types';
import {
  getAugmentedTemplates,
  PICTURE_REPORT_TEMPLATE_IDS,
  stripPictureReportArguments,
  TemplateIdMap,
  useGetReportTemplatesForContext,
} from './utils';

const extensionLu = {
  pdf: <FilePdfOutlined />,
  json: <FileOutlined />,
  xlsx: <FileExcelOutlined />,
  zip: <FileZipOutlined />,
  html: <FileTextOutlined />,
};

interface Props {
  reportContexts: ReportContext[];
  helpText?: string | null;
  disabled?: boolean;
  cacheKey?: string | null;
  shareInHorizonOrganizations?: DeliveryModeArgumentOption[];
  hasActiveImageFilters?: boolean;
  onIncludeSelectedPicturesOnlyChange?: (includeSelectedPicturesOnly: boolean) => void;
}

type ReportInputArgument = AtlasGql_CreateReportInput & { waitForCompletion?: boolean };

type AugmentReportArgument = {
  dateFilter?: string;
  weatherStandbyRate?: string;
  siteStandbyRate?: string;
  dailyBillableDelayHours?: string;
  reportArguments?: Array<{
    reportTemplateId: string;
    args?: InputMaybe<Array<AtlasGql_ReportArgument>>;
    variantId: string | undefined;
    waitForCompletion?: boolean;
  }>;
};

function augmentReportArguments({
  reportArguments,
  dateFilter,
  weatherStandbyRate,
  siteStandbyRate,
  dailyBillableDelayHours,
}: AugmentReportArgument) {
  return (
    reportArguments?.map(({ args, reportTemplateId, ...rest }) => {
      if (reportTemplateId === TemplateIdMap.PROJECT_WORK_SUMMARY_V2) {
        return {
          ...rest,
          reportTemplateId,
          args: [
            ...(!args ? [] : args),
            dateFilter && { key: 'dateFilter', value: dateFilter },
            weatherStandbyRate && { key: 'weatherStandbyRate', value: weatherStandbyRate },
            siteStandbyRate && { key: 'siteStandbyRate', value: siteStandbyRate },
            dailyBillableDelayHours && {
              key: 'dailyBillableDelayHours',
              value: dailyBillableDelayHours,
            },
          ].filter(Boolean) as AtlasGql_ReportArgument[],
        };
      } else {
        return { args, reportTemplateId, ...rest };
      }
    }) ?? []
  );
}

export function ExportReport({
  disabled = false,
  reportContexts = [],
  helpText = null,
  cacheKey = null,
  shareInHorizonOrganizations = [],
  hasActiveImageFilters = false,
  onIncludeSelectedPicturesOnlyChange,
}: Props) {
  const { t } = useTranslation();
  const message = useMessage();
  const [modalVisible, setModalVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const gqlReportContexts = reportContexts.map(({ context }) => context);
  const contextTemplates = useGetReportTemplatesForContext(gqlReportContexts);

  const { data: deliveryModesData } = useGetReportDeliveryModesQuery();

  const templatesAreNonEmpty = contextTemplates.some(({ templates }) => templates.length);

  const deliveryModes = deliveryModesData?.reportDeliveryModes || [];
  // Setting up to rename the download delivery mode to zip. Download is for single
  // report creation.
  const zipDeliveryId = deliveryModes.find(({ name }) => ['Download', 'Zip'].includes(name))?.id;

  const reportArgInfoForContextLu: { [key: string]: ReportContext } = keyBy(
    reportContexts,
    'context'
  );

  const augmentedTemplates: ReportTemplatesForContext[] = useMemo(
    () => getAugmentedTemplates(contextTemplates, reportArgInfoForContextLu),
    [contextTemplates, reportArgInfoForContextLu]
  );

  const templateZippableLookup = useMemo(() => {
    if (!contextTemplates) {
      return {};
    }
    const templates = flatten(contextTemplates.map(({ templates }) => templates));
    return templates.reduce<Record<string, boolean | undefined>>((acc, { template }) => {
      if (!template) {
        return acc;
      }
      const canZip = template?.deliveryCompatibility?.some(({ id }) => id === zipDeliveryId);
      acc[template.id] = canZip;
      return acc;
    }, {});
  }, [contextTemplates, zipDeliveryId]);

  const {
    data: pollData,
    loading,
    pollInterval,
    pollType,
    setPollType,
    setIdForPolling,
  } = usePollingReportQuery();

  useEffect(() => {
    if (!loading && pollData && pollInterval === 0) {
      switch (pollType) {
        case PollType.REPORT:
          if (pollData?.report?.status === AtlasGqlReportStatus.Failed) {
            message.error({
              key: 'report-loading',
              content: t('reports.creationFailed', 'Report creation failed.'),
              duration: 3,
            });
          } else if (
            pollData?.report?.status === AtlasGqlReportStatus.Succeeded &&
            !!pollData.report.url
          ) {
            const href = pollData.report.url;
            const downloadLink = (
              <a href={href}>
                {t(
                  'reports.reportCreatedDownload',
                  'Your report "{{name}}" was created. Click to download.',
                  {
                    name: pollData.report.name,
                  }
                )}
              </a>
            );
            message.success({
              key: 'report-loading',
              content: <>{downloadLink}</>,
              duration: 10,
            });
          }
          return;
        case PollType.DELIVERY:
          if (pollData?.report?.deliveries?.[0].status === AtlasGqlReportDeliveryStatus.Failed) {
            message.error({
              key: 'report-loading',
              content: t('reports.creationFailed', 'Report creation failed.'),
              duration: 3,
            });
          } else if (
            pollData?.report?.deliveries?.[0].status === AtlasGqlReportDeliveryStatus.Succeeded
          ) {
            if (!!pollData.report.deliveries[0].url) {
              const href = pollData.report.deliveries[0].url;
              const downloadLink = (
                <a href={href}>
                  {t(
                    'reports.reportsCreatedDownload',
                    'Your reports were created. Click to download.'
                  )}
                </a>
              );
              message.success({
                key: 'report-loading',
                content: <>{downloadLink}</>,
                duration: 10,
              });
            } else if (pollData?.report?.deliveries?.[0].deliveryMode?.name === 'Email') {
              message.success({
                key: 'report-loading',
                content: (
                  <>
                    {t(
                      'reports.reportsEmailedShortly',
                      'Your reports were created and will be emailed to you shortly.'
                    )}{' '}
                  </>
                ),
                duration: 10,
              });
            }
          }
      }
    }
  }, [pollData, loading, message, pollInterval, pollType, t]);

  const cacheUpdate = (cache: any) => {
    cache.evict({
      id: 'ROOT_QUERY',
      field: 'reports',
    });
  };

  const [createReport] = useCreateReportMutation({
    update: cacheUpdate,
    context: {
      retryAttempts: 0,
    },
  });
  const [createReports] = useCreateReportsMutation({
    update: cacheUpdate,
    context: {
      retryAttempts: 0,
    },
  });

  const getDeliveryModeArgs = useCallback(
    ({
      hermesId,
      shareInHorizonOrganizationId,
    }: {
      hermesId?: string;
      shareInHorizonOrganizationId?: string;
    }) =>
      [
        hermesId
          ? {
              key: 'hermesId',
              value: hermesId,
            }
          : null,
        shareInHorizonOrganizationId
          ? {
              key: 'organizationId',
              value: shareInHorizonOrganizationId,
            }
          : null,
      ].filter(Boolean) as AtlasGql_ReportArgument[],
    []
  );

  const getReportInputArguments = useCallback(
    ({ templateId }): ReportInputArgument[] => {
      const template = (
        flatten(contextTemplates.map(({ templates }) => templates)).find(
          ({ template: { id } }) => id === templateId
        ) || {}
      ).template;
      const { context, longRunning } = template || {};

      const reportArguments = context ? reportArgInfoForContextLu[context]?.reportArguments : [];

      // Picture-scoped arguments (image filters + inverter boundary args) live on the
      // shared context arguments, so scope them to picture reports only — a non-picture
      // report in a mixed export must not inherit them.
      const isPictureReport = PICTURE_REPORT_TEMPLATE_IDS.includes(templateId);

      return (reportArguments || []).map(({ args }: { args: Array<AtlasGql_ReportArgument> }) => ({
        reportTemplateId: templateId,
        args: isPictureReport ? args : stripPictureReportArguments(args),
        // Each report on its own will provide whether it prefers to be long
        // running. We make a decision at the end to let the reports run sync
        // or not.
        waitForCompletion: !longRunning,
      }));
    },
    [contextTemplates, reportArgInfoForContextLu]
  );

  const handleCreateReportTemplates = async ({
    reportTemplates,
    deliveryModeId: inputDeliveryModeId,
    hermesId,
    shareInHorizonOrganizationId,
    dateFilter,
    weatherStandbyRate,
    siteStandbyRate,
    dailyBillableDelayHours,
  }: {
    reportTemplates: SubmitReportTemplateProp[];
    deliveryModeId?: string;
    hermesId?: string;
    shareInHorizonOrganizationId?: string;
    dateFilter?: string;
    weatherStandbyRate?: string;
    siteStandbyRate?: string;
    dailyBillableDelayHours?: string;
  }) => {
    let reportArguments = flatten(
      (reportTemplates || []).map(({ id: templateId, variantId }) =>
        getReportInputArguments({ templateId }).map((input: ReportInputArgument) => ({
          ...input,
          variantId,
        }))
      )
    );
    onIncludeSelectedPicturesOnlyChange?.(false);
    setModalVisible(false);

    reportArguments = augmentReportArguments({
      reportArguments,
      dateFilter,
      weatherStandbyRate,
      siteStandbyRate,
      dailyBillableDelayHours,
    });

    if (!reportArguments.length) {
      // Not sure how to get into this scenario, but figured i'd handle it anyway
      message.success({
        key: 'report-loading',
        content: t('reports.noReportsToQueue', 'There are no reports to queue ... '),
        duration: 3,
      });
    } else if (reportArguments.length === 1) {
      // Single report creation.
      message.loading({
        key: 'report-loading',
        content: t(
          'reports.creatingReport',
          'Creating Report. You will be notified upon completion.'
        ),
        duration: 3,
      });

      try {
        const [input] = reportArguments;
        const deliveryModeArgs = getDeliveryModeArgs({
          hermesId,
          shareInHorizonOrganizationId,
        });

        // shouldPoll = it is not a long running report
        const shouldPoll = input.waitForCompletion && !inputDeliveryModeId;

        const { data } = await createReport({
          variables: assign(
            {
              ...input,
              deliveryModeId: inputDeliveryModeId,
              waitForCompletion: false,
            },
            deliveryModeArgs.length
              ? {
                  deliveryModeArgs,
                }
              : {}
          ),
        });
        const report = data?.createReportAsync;
        if (!report) {
          throw new Error('Failed to create report');
        }

        if (shouldPoll) {
          // not long running -> polling
          if (report.id) {
            setPollType(PollType.REPORT);
            setIdForPolling(report.id);
          }
        } else {
          // long running -> no polling
          // The report was queued. Tell the user where to find it when its done.
          const href = `/reports/${report.id}`;
          message.success({
            key: 'report-loading',
            content: (
              <Trans
                i18nKey="reports.reportAvailableHere"
                defaults="Your report will be available <a>here</a> shortly."
                components={{
                  // content is injected by Trans at runtime
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  a: <a href={href} />,
                }}
              />
            ),
            duration: 3,
          });
        }
      } catch (err) {
        message.error({
          key: 'report-loading',
          content: t('reports.creationFailed', 'Report creation failed.'),
          duration: 3,
        });
      }
    } else {
      // Multi report creation.
      message.info({
        key: 'report-loading',
        content: t('reports.queueingReportsAsync', 'Queueing reports for async delivery...'),
        duration: 3,
      });

      try {
        const deliveryModeArgs = getDeliveryModeArgs({
          hermesId,
          shareInHorizonOrganizationId,
        });
        // shouldPoll = there are no long running reports
        const shouldPoll = reportArguments.every(r => r?.waitForCompletion);
        const canZip = reportArguments.every(r => templateZippableLookup[r.reportTemplateId]);

        // For multiple reports, we force Zip as the delivery mode unless the
        // user has made an explicit alternate choice via the advanced reports
        // page or if some reports are not zippable.
        const deliveryModeId = inputDeliveryModeId || (canZip ? zipDeliveryId : null);

        const { data } = await createReports({
          variables: assign(
            {
              inputs: reportArguments.map(({ reportTemplateId, variantId, args }) => ({
                reportTemplateId,
                variantId,
                args,
              })),
              deliveryModeId,
              waitForCompletion: false,
            },
            deliveryModeArgs.length
              ? {
                  deliveryModeArgs,
                }
              : {}
          ),
        });

        // If we're waiting, we can expect the service to have not returned until
        // the delivery is complete. All reports will share the same delivery, so
        // its fine to just grab the first one.
        const reports = data?.createReportsAsync;
        const firstReport = reports?.[0];
        const delivery = firstReport?.deliveries?.[0];

        if (shouldPoll) {
          if (firstReport?.id) {
            setPollType(PollType.DELIVERY);
            setIdForPolling(firstReport.id);
          }
        } else {
          // At this point, there could be multiple reports being delivered in a
          // single delivery, or just multiple reports.
          let content;
          if (deliveryModeId) {
            const deliveryHref = `/report-deliveries/${delivery?.id}`;
            content =
              deliveryModeId === zipDeliveryId ? (
                <Trans
                  i18nKey="reports.reportsQueuedZipReady"
                  defaults="Reports queued successfully. They will be ready <a>here</a> shortly."
                  components={{
                    // content is injected by Trans at runtime
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    a: <a href={deliveryHref} />,
                  }}
                />
              ) : (
                <Trans
                  i18nKey="reports.reportsQueuedDeliveryStatus"
                  defaults="Reports queued successfully. You will be notified when the delivery is complete or you may check on its status <a>here</a>."
                  components={{
                    // content is injected by Trans at runtime
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    a: <a href={deliveryHref} />,
                  }}
                />
              );
          } else {
            content = canZip ? (
              <Trans
                i18nKey="reports.reportsQueuedReady"
                defaults="Reports queued successfully. They will be ready <a>here</a> shortly."
                components={{
                  // content is injected by Trans at runtime
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  a: <a href="/reports" />,
                }}
              />
            ) : (
              <Trans
                i18nKey="reports.reportsQueuedCannotZip"
                defaults="Reports queued successfully. They cannot be zipped. They will be ready <a>here</a> shortly."
                components={{
                  // content is injected by Trans at runtime
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  a: <a href="/reports" />,
                }}
              />
            );
          }
          message.success({
            key: 'report-loading',
            content,
            duration: 3,
          });
        }
      } catch (err) {
        message.error({
          key: 'report-loading',
          content: t('reports.creationFailed', 'Report creation failed.'),
          duration: 3,
        });
      }
    }
  };

  const handleCloseAdvancedReportOptions = () => {
    onIncludeSelectedPicturesOnlyChange?.(false);
    setModalVisible(false);
  };

  return (
    <>
      <Modal
        title={t('reports.advancedReportOptions', 'Advanced Report Options')}
        open={modalVisible}
        footer={null}
        onCancel={handleCloseAdvancedReportOptions}
        destroyOnHidden
      >
        <AdvancedReportForm
          templates={augmentedTemplates}
          onSubmit={handleCreateReportTemplates}
          onCancel={handleCloseAdvancedReportOptions}
          cacheKey={cacheKey}
          reportContexts={reportContexts}
          shareInHorizonOrganizations={shareInHorizonOrganizations}
          hasActiveImageFilters={hasActiveImageFilters}
          onIncludeSelectedPicturesOnlyChange={onIncludeSelectedPicturesOnlyChange}
        />
      </Modal>
      <Tooltip
        title={
          !templatesAreNonEmpty
            ? t('reports.noReportTemplatesAvailable', 'There are no report templates available')
            : helpText
        }
      >
        <Button style={{ padding: '0' }} disabled={disabled || !templatesAreNonEmpty}>
          <Dropdown
            trigger={['click']}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            menu={{
              items: [
                ...augmentedTemplates.map(({ context, templates }) => ({
                  type: 'group' as MenuItemGroupType['type'],
                  label: startCase(lowerCase(context)),
                  children: (templates ?? []).map(({ template, disabled }) => {
                    const { id, name, longRunning, extension, itemLimit } = template;

                    const matchingContext = reportContexts.find(
                      reportContext => reportContext?.context === context
                    );
                    const shouldDisableOption = matchingContext && matchingContext?.optionDisabled;
                    return {
                      key: id,
                      disabled: disabled || shouldDisableOption,
                      label: (
                        <Space>
                          <Tooltip
                            title={
                              disabled
                                ? matchingContext?.toolTip
                                  ? matchingContext?.toolTip
                                  : t(
                                      'reports.reportsLimitedToItems',
                                      '{{name}} reports are limited to {{itemLimit}} items',
                                      {
                                        name: `${startCase(lowerCase(context))}:${name}`,
                                        itemLimit,
                                      }
                                    )
                                : null
                            }
                          >
                            {extension && extensionLu[extension]}
                            &nbsp;
                            {name}
                          </Tooltip>
                          {longRunning && (
                            <Tooltip
                              title={t(
                                'reports.longRunningReportTooltip',
                                'This report may take a while to generate. You will be notified when it is complete.'
                              )}
                            >
                              <Tag color="gold">{t('reports.async', 'Async')}</Tag>
                            </Tooltip>
                          )}
                        </Space>
                      ),
                    };
                  }),
                })),
                {
                  key: 'advanced',
                  label: t('reports.advanced', 'Advanced'),
                },
              ],
              onClick: ({ key }) => {
                if (key === 'advanced') {
                  setModalVisible(true);
                } else {
                  handleCreateReportTemplates({
                    reportTemplates: [{ id: key.toString() }],
                  });
                }
                setMenuOpen(false);
              },
            }}
          >
            <div style={{ padding: '0 1em' }}>
              {t('reports.exportReport', 'Export Report')}&nbsp;
              <DownOutlined />
            </div>
          </Dropdown>
        </Button>
      </Tooltip>
    </>
  );
}
